// IAP product catalog (Apple / Google SKUs).
// Keep ids stable — wire the same productIds in App Store Connect,
// Play Console, and RevenueCat later.
// Treats are earned in play and are not sold here.

import {
    CORGI_PACK_DUPLICATE_REFUND,
    formatCorgiPackBundleTitle,
    listCorgiPackBundles,
} from "./treats";

export const IAP_PRODUCT_TYPES = {
    CONSUMABLE: "consumable",
    NON_CONSUMABLE: "non_consumable",
    SUBSCRIPTION: "subscription",
};

export const IAP_PRODUCT_CATEGORIES = {
    PACKS: "packs",
    HEARTS: "hearts",
    BOOSTS: "boosts",
    EXTRAS: "extras",
};

export const NO_ADS_PRODUCT_ID = "no_ads";
export const HEART_TEN_PRODUCT_ID = "hearts_10";
export const BOOST_BUNDLE_IAP_PRODUCT_ID = "boost_bundle_1";

const PACK_IAP_PRICE_BY_COUNT = {
    1: "$0.99",
    3: "$2.99",
    5: "$4.99",
    10: "$8.99",
};

function buildPackIapProducts() {
    return listCorgiPackBundles().map((bundle, index) => {
        const id = `packs_${bundle.packCount}`;
        return {
            id,
            sku: id,
            type: IAP_PRODUCT_TYPES.CONSUMABLE,
            category: IAP_PRODUCT_CATEGORIES.PACKS,
            title: formatCorgiPackBundleTitle(bundle.packCount),
            description: `${bundle.cardCount} cards with the same odds as an Endless board. Open now or save to Inventory. Duplicates refund ${CORGI_PACK_DUPLICATE_REFUND.toLocaleString("en-US")} Treats.`,
            hearts: 0,
            treats: 0,
            packs: bundle.packCount,
            refillToMax: false,
            previewPriceLabel: PACK_IAP_PRICE_BY_COUNT[bundle.packCount],
            sort: 5 + index,
            enabled: true,
        };
    });
}

/**
 * Heart packs for the F2P battery. Prices are preview labels only until
 * Capacitor + store billing returns localized pricing.
 */
export const IAP_PRODUCTS = [
    ...buildPackIapProducts(),
    {
        id: "hearts_1",
        sku: "hearts_1",
        type: IAP_PRODUCT_TYPES.CONSUMABLE,
        category: IAP_PRODUCT_CATEGORIES.HEARTS,
        title: "+1 Heart",
        description:
            "Add one heart to your stash. Can go above the natural 3-heart regen limit.",
        hearts: 1,
        treats: 0,
        packs: 0,
        refillToMax: false,
        previewPriceLabel: "$0.99",
        sort: 10,
        enabled: true,
    },
    {
        id: "hearts_3",
        sku: "hearts_3",
        type: IAP_PRODUCT_TYPES.CONSUMABLE,
        category: IAP_PRODUCT_CATEGORIES.HEARTS,
        title: "+3 Hearts",
        description:
            "Add three hearts to your stash. Can go above the natural 3-heart regen limit.",
        hearts: 3,
        treats: 0,
        packs: 0,
        refillToMax: false,
        previewPriceLabel: "$1.99",
        sort: 20,
        enabled: true,
    },
    {
        id: HEART_TEN_PRODUCT_ID,
        sku: HEART_TEN_PRODUCT_ID,
        type: IAP_PRODUCT_TYPES.CONSUMABLE,
        category: IAP_PRODUCT_CATEGORIES.HEARTS,
        title: "+10 Hearts",
        description:
            "Add ten hearts to your stash. Can go above the natural 3-heart regen limit.",
        hearts: 10,
        treats: 0,
        packs: 0,
        refillToMax: false,
        previewPriceLabel: "$7.99",
        sort: 30,
        enabled: true,
    },
    {
        id: BOOST_BUNDLE_IAP_PRODUCT_ID,
        sku: BOOST_BUNDLE_IAP_PRODUCT_ID,
        type: IAP_PRODUCT_TYPES.CONSUMABLE,
        category: IAP_PRODUCT_CATEGORIES.BOOSTS,
        title: "Boost Bundle",
        description:
            "1 Reveal + 1 Shield + 1 Freeze. Added to Inventory for use during Story and Endless runs.",
        hearts: 0,
        treats: 0,
        packs: 0,
        boostBundle: 1,
        refillToMax: false,
        previewPriceLabel: "$1.99",
        sort: 35,
        enabled: true,
    },
    {
        id: NO_ADS_PRODUCT_ID,
        sku: NO_ADS_PRODUCT_ID,
        type: IAP_PRODUCT_TYPES.NON_CONSUMABLE,
        category: IAP_PRODUCT_CATEGORIES.EXTRAS,
        title: "No Ads",
        description:
            "Remove rewarded ads from heart refills. Heart packs still work. One-time purchase.",
        hearts: 0,
        treats: 0,
        packs: 0,
        refillToMax: false,
        removeAds: true,
        previewPriceLabel: "$4.99",
        sort: 40,
        enabled: true,
    },
];

const BY_ID = IAP_PRODUCTS.reduce((map, product) => {
    map[product.id] = product;
    return map;
}, {});

export function getIapProductById(productId) {
    if (!productId) {
        return null;
    }
    return BY_ID[String(productId)] || null;
}

export function listIapProducts({ category = null, enabledOnly = true } = {}) {
    return IAP_PRODUCTS.filter((product) => {
        if (enabledOnly && product.enabled === false) {
            return false;
        }
        if (category && product.category !== category) {
            return false;
        }
        return true;
    }).sort((a, b) => (a.sort || 0) - (b.sort || 0));
}

export function getHeartRefillProductId() {
    return "hearts_3";
}

export function getHeartSingleProductId() {
    return "hearts_1";
}

export function getHeartTenProductId() {
    return HEART_TEN_PRODUCT_ID;
}

export function getNoAdsProductId() {
    return NO_ADS_PRODUCT_ID;
}

export function getBoostBundleIapProductId() {
    return BOOST_BUNDLE_IAP_PRODUCT_ID;
}

export function getPackIapProductId(packCount = 1) {
    return `packs_${packCount}`;
}
